import React from "react";

type BadgeType = "application" | "payment" | "role" | "email";
type BadgeSize = "sm" | "md";

interface StatusBadgeProps {
  status: string;
  type?: BadgeType;
  size?: BadgeSize;
}

const applicationColors: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-700 ring-gray-200",
  PENDING_REVIEW: "bg-orange-50 text-orange-700 ring-orange-200",
  APPROVED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  SUBMITTED: "bg-blue-50 text-blue-700 ring-blue-200",
  UNDER_REVIEW: "bg-amber-50 text-amber-700 ring-amber-200",
  ACCEPTED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  REJECTED: "bg-red-50 text-red-700 ring-red-200",
  WAITLISTED: "bg-purple-50 text-purple-700 ring-purple-200",
  WITHDRAWN: "bg-gray-100 text-gray-500 ring-gray-200",
};

const paymentColors: Record<string, string> = {
  COMPLETED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  PENDING: "bg-amber-50 text-amber-700 ring-amber-200",
  FAILED: "bg-red-50 text-red-700 ring-red-200",
  NOT_REQUIRED: "bg-gray-100 text-gray-500 ring-gray-200",
};

const roleColors: Record<string, string> = {
  ADMIN: "bg-red-50 text-red-700 ring-red-200",
  UNIVERSITY_REP: "bg-blue-50 text-blue-700 ring-blue-200",
  AGENT: "bg-amber-50 text-amber-700 ring-amber-200",
  STUDENT: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

const emailColors: Record<string, string> = {
  VERIFIED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  UNVERIFIED: "bg-red-50 text-red-700 ring-red-200",
};

const colorMaps: Record<BadgeType, Record<string, string>> = {
  application: applicationColors,
  payment: paymentColors,
  role: roleColors,
  email: emailColors,
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  type = "application",
  size = "sm",
}) => {
  const colors = colorMaps[type]?.[status] || "bg-gray-100 text-gray-600 ring-gray-200";
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm";

  const label = status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ring-1 ring-inset ${colors} ${sizeClasses}`}
    >
      {label}
    </span>
  );
};

export default StatusBadge;
